import { notifications } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import useImmediateAction from 'api/use-immediate-action';
import HierarchyForm, { HierarchyFormData } from 'components/forms/hierarchy/HierarchyForm';
import RequireGlobalPermissionPage from 'components/permissions/RequireGlobalPermissionPage';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { Navigate, useParams } from 'react-router-dom';
import { HIERARCHIES_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/** Edits stable hierarchy levels, labels and exact organization assignments. */
export default function EditHierarchyPage() {
  const { hierarchyId } = useParams();
  const { getAction } = useApi();
  const { data, loading, error } = useImmediateAction(() =>
    getAction('HierarchyGet')({ parameters: { hierarchyId: hierarchyId! } })
  );

  if (!hierarchyId) return <Navigate to={HIERARCHIES_PAGE_PATH.original} />;
  if (error) {
    panic(error);
    return null;
  }

  /** Replaces labels and direct assignments. */
  const onSubmit = ({ hierarchyName, levels, organizationIds }: HierarchyFormData) =>
    getAction('HierarchyUpdate')({
      parameters: { hierarchyId },
      payload: { hierarchyName, levels, organizationIds: organizationIds.map(Number) },
    })
      .then(() => {
        notifications.show({
          title: 'Hierarchia bola upravená',
          message: `Hierarchia ${hierarchyName} bola úspešne upravená.`,
          color: SUCCESS_NOTIFICATION_COLOR,
        });
      })
      .catch(panic);

  return (
    <RequireGlobalPermissionPage permission="manage-hierarchies">
      <DashboardLayout
        title={`Úprava hierarchie: ${data?.hierarchyName ?? ''}`}
        breadcrumbs={[
          { title: 'Hierarchie', link: HIERARCHIES_PAGE_PATH.original },
          { title: data?.hierarchyName ?? 'Načítavanie ...' },
        ]}
      >
        {!loading && data && (
          <HierarchyForm
            initialValues={{
              hierarchyName: data.hierarchyName,
              levels: data.levels.map(({ hierarchyLevelId, label }) => ({ hierarchyLevelId, label })),
              organizationIds: data.directOrganizations.map(({ organizationId }) => String(organizationId)),
            }}
            structureFrozen={data.structureFrozen}
            onSubmit={onSubmit}
          />
        )}
      </DashboardLayout>
    </RequireGlobalPermissionPage>
  );
}
